import { get } from 'svelte/store';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { devices_config } from '@devices/index';
import {
	get_device_component_index,
	get_gen_2_control_component_channel_structure,
	get_mode,
	get_z_wave_channel_structure_from_category,
	is_dali,
	is_gen_2,
	is_gen_z,
	isCctOnlyDevice,
	isProThreeEM,
	isRgbCct,
	isRgbwwPm,
	isXMODDevice,
	real_id,
} from 'src/helpers/interface_helpers';
import type { Device } from 'src/types/Device';

export const mapLightGroupDevices = (groupDevicesIDList: string[]) => {
	return groupDevicesIDList
		?.map((id) => {
			let baseId = id;
			let channel = 0;
			if (id.includes('_')) {
				const idMatch = id.match(/^(.+)_(\d+)$/);
				if (idMatch) {
					baseId = idMatch[1];
					channel = parseInt(idMatch[2], 10);
				}
			}
			if (get(devices)[baseId]) {
				const channelDevice = get(devices)[id];
				const baseDevice = get(devices)[baseId];
				const device = channelDevice
					? { ...baseDevice, ...channelDevice, id, channel }
					: { ...baseDevice, id, channel };

				return device;
			}
			if (!get(devices)[baseId]) {
				return { id: id, deleted: true };
			}
		})
		?.filter((e) => e !== undefined);
};

export const getLightCategoryDevices = (
	devicesList: Device[],
	devicesStatus = get(devices_status),
	devicesSettings = get(devices_settings),
	devicesStore = get(devices),
) => {
	return devicesList
		?.filter((device) => {
			const isLightCategory = device.category === 'light' || isCctOnlyDevice(device.type);
			let isXMODLight = false;
			if (isXMODDevice(device?.type)) {
				const realId = real_id(device.id);
				const status = devicesStatus?.[realId];
				if (status?.sys?.ch && Array.isArray(status.sys.ch) && status.sys.ch.length > 0) {
					const channelIndex = device.channel ?? 0;
					const component = status.sys.ch[channelIndex] || status.sys.ch[0];
					if (component) {
						const componentType = component.split(':')[0];
						isXMODLight =
							componentType === 'light' ||
							componentType === 'cct' ||
							componentType === 'rgbw';
					}
				}
			}
			return (isLightCategory || isXMODLight) && devicesStatus?.[real_id(device.id)]?.online;
		})
		.map((d) => {
			let mode = '';
			if (isXMODDevice(d?.type)) {
				const realId = real_id(d.id);
				const status = devicesStatus?.[realId];
				if (status?.sys?.ch && Array.isArray(status.sys.ch) && status.sys.ch.length > 0) {
					const channelIndex = d.channel ?? 0;
					const component = status.sys.ch[channelIndex] || status.sys.ch[0];
					if (component) {
						const componentType = component.split(':')[0];
						if (componentType === 'cct') {
							mode = 'cct';
						} else if (componentType === 'rgbw' || componentType === 'light') {
							mode = 'rgbw';
						} else {
							mode = 'shell';
						}
					} else {
						mode = d.mode || 'shell';
					}
				} else {
					mode = d.mode || 'shell';
				}
			} else if (devicesStatus?.[real_id(d.id)] && devicesSettings?.[real_id(d.id)]) {
				const baseDeviceId = real_id(d.id);
				mode = get_mode(
					devicesStore?.[baseDeviceId],
					devicesStatus?.[baseDeviceId],
					devicesSettings?.[baseDeviceId],
				);
			} else {
				const baseDeviceId = real_id(d.id);
				mode = Object.keys(devices_config[devicesStore?.[baseDeviceId]?.type]?.modes || {})[0];
			}

			// don't override mode for Pro RGBWW PM
			mode = isRgbwwPm(d.type) ? d.mode : mode;

			let slider;
			if (isXMODDevice(d?.type)) {
				if (mode === 'rgbw' || mode === 'cct') {
					slider = 'brightness';
				} else {
					slider = devices_config[d?.type]?.modes?.[mode]?.slider;
				}
			} else {
				slider = devices_config[d?.type]?.modes?.[mode]?.slider;
			}

			return {
				...d, //device must be first, because overrides mode with color everytime
				mode,
				slider,
			};
		});
};

export const getColorModeLights = (lightsCategoryDevices: any[]) => {
	return lightsCategoryDevices?.filter((d) => {
		const mode = get_mode(d, readDeviceStatus(d.id), readDeviceSettings(d.id));
		const _isRgbCct =
			isRgbCct(d.type) && devices_settings.getByRealId(d.id)?.[`${mode}:0`]?.mode === 'rgb';
		return (
			d.mode === 'color' ||
			d.mode === 'rgb' ||
			d.mode === 'rgbw' ||
			mode === 'rgb' ||
			mode === 'rgbw' ||
			_isRgbCct
		);
	});
};

export const getWhiteChannelSupportLights = (colorModeLights: any[]) => {
	return colorModeLights?.filter((device) => {
		const mode = get_mode(device, readDeviceStatus(device.id), readDeviceSettings(device.id));
		return devices_config[device?.type]?.modes?.[mode]?.has_white;
	});
};

export const checkLightGroupHasSingleSlider = (group) => {
	const list = mapLightGroupDevices(group?.devices);

	const lightsCategoryDevices = getLightCategoryDevices(list as Device[]);

	const colorModeLights = getColorModeLights(lightsCategoryDevices);

	const white_channel_support_lights = getWhiteChannelSupportLights(colorModeLights);

	return (
		lightsCategoryDevices.filter((d) => d.slider !== undefined).length > 0 &&
		white_channel_support_lights.length < 1
	);
};

export const getGroupDeviceState = (category, device, status) => {
	// Used instead of 'device.channel' because there are devices without consistency between their control component and device channel
	const controlComponentIndex = get_device_component_index(device);
	if (category === undefined) return false;
	if (!status?.online) return undefined;
	if (category === 'roller') {
		let statusRoller = status['cover:0'];
		if (status.hasOwnProperty('rollers')) statusRoller = status.rollers[0];
		if (statusRoller?.current_pos > -1 && statusRoller?.current_pos < 101) {
			return statusRoller?.current_pos > 0;
		} else if (statusRoller?.last_direction === 'open') {
			return true;
		} else if (statusRoller?.last_direction === 'close') {
			return false;
		} else {
			return false;
		}
	} else if (typeof status[category.split('s_')[0] + 's'] !== 'undefined') {
		switch (category) {
			case 'thermostat':
				return status.thermostats !== undefined;
			default:
				return status[category.split('s_')[0] + 's'][
					device.category === 'emeter' ? 0 : get_device_component_index(device)
				].ison;
		}
	} else if (
		is_gen_2(device) &&
		category === 'light' &&
		typeof status[get_gen_2_control_component_channel_structure(device)] !== undefined
	) {
		return status?.[get_gen_2_control_component_channel_structure(device)]?.output;
	} else if (isProThreeEM(device?.type) && typeof status[`switch:100`] !== 'undefined') {
		return status?.[`switch:100`]?.output;
	} else if (typeof status[`switch:${controlComponentIndex}`] !== 'undefined') {
		return status?.[`switch:${controlComponentIndex}`]?.output;
	} else if (typeof status[`cover:${controlComponentIndex}`] !== 'undefined') {
		return status?.[`switch:${controlComponentIndex}`]?.current_pos !== 0;
	} else if (typeof status[`cb:${controlComponentIndex}`] !== 'undefined') {
		//Adding a safety check for circuit breakers. Maybe it's not the best way to do it but at the moment it's the best I can think of
		if (status[`cb:${controlComponentIndex}`]?.safety === true) {
			status['locked'] = true;
		} else {
			delete status['locked'];
		}
		status.ison = status[`cb:${controlComponentIndex}`]?.output;
		return status;
	} else if (typeof status[`light:${controlComponentIndex}`] !== 'undefined') {
		if (is_dali(device)) {
			if (status?.dali?.errors?.length > 0 || status?.dali?.cg_count === 0) {
				return undefined;
			}
		} //status icons for DALI devices gen 3 with errors or no control gear
		return status?.[`light:${controlComponentIndex}`]?.output;
	} else if (typeof status[`cct:${controlComponentIndex}`] !== 'undefined') {
		return status?.[`cct:${controlComponentIndex}`]?.output;
	} else if (typeof status[`rgb:${controlComponentIndex}`] !== 'undefined') {
		return status?.[`rgb:${controlComponentIndex}`]?.output;
	} else if (typeof status[`rgbw:${controlComponentIndex}`] !== 'undefined') {
		return status?.[`rgbw:${controlComponentIndex}`]?.output;
	} else if (typeof status?.[device.category + 's']?.[controlComponentIndex] !== 'undefined') {
		//SHSW mode light
		return status[device.category + 's'][controlComponentIndex].ison;
	} else if (is_gen_z(device)) {
		const component = get_z_wave_channel_structure_from_category(device);
		if (device.category !== 'roller') {
			return status[component].currentValue;
		} else {
			return status?.[component]?.currentValue < 99;
		}
	} else {
		return status;
	}
};

export const DASH_LIGHT_WIDGET_BG = [
	'--gradient-red-purple',
	'--gradient-royal-purple',
	'--gradient-orange-magenta',
	'--gradient-blue-green',
];

export const getRandomDashLightWidgetBg = () =>
	DASH_LIGHT_WIDGET_BG[Math.floor(Math.random() * DASH_LIGHT_WIDGET_BG.length)];
